import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  DefaultButton,
  mergeStyles,
  Pivot,
  PivotItem,
  ScrollablePane,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { SoftwarePackageVersion } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useSoftwarePackageVersions } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { SoftwarePackageDeploymentRings } from "./deployment-rings";
import { SoftwarePackageDetailsCard } from "./SoftwarePackageDetailsCard";
import { SoftwarePackageVersionsTable } from "./SoftwarePackageVersionsTable";
import {
  SoftwarePackageVersionTableFilter,
  SoftwarePackageVersionTableFilterValues
} from "./SoftwarePackageVersionTableFilter";

enum SoftwarePackageVersionsPageTabs {
  Versions = "versions",
  DeploymentRings = "deployment-rings"
}

// Every level between the page and the tables' ScrollablePane (Pivot wrapper, tab panel
// and PivotItem div) must fill the remaining height, otherwise the pane collapses to 0.
const fillColumnStyle = {
  display: "flex",
  flexDirection: "column" as const,
  flexGrow: 1,
  minHeight: 0
};

const fillColumnClassName = mergeStyles(fillColumnStyle);

export const SoftwarePackageVersionsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { softwarePackageId } = useParams<{ softwarePackageId: string }>();
  const [filtersArgs, setFilterArgs] = useState<
    SoftwarePackageVersionTableFilterValues
  >({
    version: ""
  });

  const softwarePackageVersionsQuery = useSoftwarePackageVersions({
    softwarePackageId: softwarePackageId ? softwarePackageId : undefined
  });

  return (
    <Stack
      verticalFill
      styles={{
        root: { padding: theme.spacing.s1, width: "100%" }
      }}
      tokens={{ childrenGap: theme.spacing.m }}
    >
      <QueryStateIndicator {...softwarePackageVersionsQuery}>
        {softwarePackageVersions => {
          const softwarePackageName =
            softwarePackageVersions?.[0]?.softwarePackageDisplayName ??
            "Unknown Software Package";

          return (
            <>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: theme.spacing.l1 }}
              >
                <DefaultButton onClick={() => navigate("..")}>
                  Back to Software Packages
                </DefaultButton>

                <SectionTitle>{softwarePackageName}</SectionTitle>
              </Stack>

              <SoftwarePackageDetailsCard
                softwarePackageVersion={softwarePackageVersions?.[0]}
              />

              <Pivot
                defaultSelectedKey={SoftwarePackageVersionsPageTabs.Versions}
                className={fillColumnClassName}
                styles={{ itemContainer: fillColumnStyle }}
              >
                <PivotItem
                  itemKey={SoftwarePackageVersionsPageTabs.Versions}
                  headerText="Versions"
                  className={fillColumnClassName}
                >
                  <Stack
                    grow
                    tokens={{ childrenGap: theme.spacing.m }}
                    styles={{
                      root: { minHeight: 0, paddingTop: theme.spacing.m }
                    }}
                  >
                    <SoftwarePackageVersionTableFilter
                      setFilterArgs={setFilterArgs}
                    />
                    <Stack.Item
                      grow
                      styles={{ root: { position: "relative", minHeight: 0 } }}
                    >
                      <ScrollablePane>
                        <SoftwarePackageVersionsTable
                          items={filterAndSortRolloutSoftwarePackageVersions(
                            softwarePackageVersions ?? [],
                            filtersArgs
                          )}
                        />
                      </ScrollablePane>
                    </Stack.Item>
                  </Stack>
                </PivotItem>

                <PivotItem
                  itemKey={SoftwarePackageVersionsPageTabs.DeploymentRings}
                  headerText="Deployment Rings"
                  className={fillColumnClassName}
                >
                  <Stack
                    grow
                    styles={{
                      root: { minHeight: 0, paddingTop: theme.spacing.m }
                    }}
                  >
                    <SoftwarePackageDeploymentRings
                      softwarePackageId={softwarePackageId!}
                      softwarePackageVersions={softwarePackageVersions ?? []}
                    />
                  </Stack>
                </PivotItem>
              </Pivot>
            </>
          );
        }}
      </QueryStateIndicator>
    </Stack>
  );
};

const filterAndSortRolloutSoftwarePackageVersions = (
  rolloutSoftwarePackageVersions: SoftwarePackageVersion[],
  filter: SoftwarePackageVersionTableFilterValues
): SoftwarePackageVersion[] => {
  return rolloutSoftwarePackageVersions
    .filter(
      d =>
        filter.version === "" ||
        d.version.toLowerCase().indexOf(filter.version.toLowerCase()) >= 0
    )
    .sort((a: SoftwarePackageVersion, b: SoftwarePackageVersion) => {
      return a.version < b.version ? 1 : -1;
    });
};
